import React from 'react';
import { Box, Typography, FormControl, Select, MenuItem } from '@mui/material';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import CategorySummaryCard from '../../../components/reporting/CategorySummaryCard';

export default function ByCategoryReport(props) {
  const {
    selectedCategory,
    adjustedTeamsMap,
    adjustedRows,
    teams,
    categoriesMap,
    divisionsMap,
    eventsScores,
    eventHasAdjustments,
    teamModes,
    setTeamModes,
    setAutoAppliedModes,
    setManualOverrides,
    affiliatedMode
  } = props;

  // Columns must be created via hooks before any early return
  const columns = React.useMemo(() => {
    const cols = [
      { field: 'teamNumber', headerName: 'Team Number', width: 140 },
      { field: 'teamName', headerName: 'Team Name', width: 300 },
      { field: 'division', headerName: 'Division', width: 160 },
      { field: 'affiliated', headerName: 'Affiliated', width: 120, renderCell: (params) => {
        return params.value ? <CheckCircleIcon sx={{ color: 'green' }} /> : <CancelIcon sx={{ color: 'red' }} />;
      } },
      {
        field: 'mode', headerName: 'Mode', width: 220, renderCell: (params) => {
          const teamUid = params.row?.id;

          const isAffiliatedAny = Object.keys(eventsScores || {}).some(evUid => {
            const assoc = (eventsScores[evUid]?.event?.associatedTeams || []).map(a => (typeof a === 'string' ? a : a.uid));
            return assoc.includes(teamUid);
          });

          const value = teamModes[teamUid] || 'normal';
          return (
            <FormControl variant="standard" sx={{ minWidth: 180 }}>
              <Select
                value={value}
                onChange={(e) => {
                  const v = e.target.value;
                  if (setManualOverrides) {
                    setManualOverrides(prev => ({ ...(prev || {}), [teamUid]: true }));
                  }
                  if (setAutoAppliedModes) {
                    setAutoAppliedModes(prev => {
                      const copy = { ...(prev || {}) };
                      delete copy[teamUid];
                      return copy;
                    });
                  }
                  setTeamModes(prev => ({ ...prev, [teamUid]: v }));
                }}
                size="small"
              >
                <MenuItem value="normal">Normal</MenuItem>
                <MenuItem value="total_average">Total Average</MenuItem>
                <MenuItem value="affiliated_average" disabled={!isAffiliatedAny}>Affiliated Average (if below avg)</MenuItem>
                <MenuItem value="affiliated_max" disabled={!isAffiliatedAny}>Affiliated Max (worst score)</MenuItem>
                <MenuItem value="affiliated_up_next" disabled={!isAffiliatedAny}>Affiliated Up Next (next non-affiliated score)</MenuItem>
                <MenuItem value="exclude" disabled={!isAffiliatedAny}>Affiliated Exclude (set to 1500)</MenuItem>
              </Select>
            </FormControl>
          );
        }
      },
      { field: 'originalTotal', headerName: 'Original Total', width: 140 },
    ];

    if (eventHasAdjustments) {
      cols.push({ field: 'adjustedTotal', headerName: 'Adjusted Total', width: 160 });
    }

    // Category placements (competition-level within this category)
    cols.push({ field: 'categoryPlace', headerName: 'Category Place', width: 140 });
    if (eventHasAdjustments) {
      cols.push({ field: 'categoryPlaceAssociated', headerName: 'Adjusted Category Place', width: 160 });
    }

    return cols;
  }, [eventHasAdjustments, teamModes, eventsScores, divisionsMap, setTeamModes, setAutoAppliedModes, setManualOverrides]);

  if (!selectedCategory) return <Typography>Please select a category</Typography>;

  // Filter teams by the selected category (team.category is the uid)
  const rowsBase = Object.values(adjustedTeamsMap || {}).filter(t => t.category === selectedCategory).map(t => ({
    id: t.uid,
    teamNumber: t.teamNumber,
    teamName: t.teamName,
    division: divisionsMap[t.division] || t.division,
    affiliated: t.affiliated,
    originalTotal: t.originalTotal,
    adjustedTotal: t.adjustedTotal,
    mode: t.mode
  }));

  // Compute category placements (exclude teams with 0 score)
  const getPlaces = (arr, key) => {
    const scores = arr.map(r => ({ id: r.id, score: Number(r[key] || 0) }));
    const uniqueScores = Array.from(new Set(scores.filter(s => s.score !== 0).map(s => s.score))).sort((a, b) => a - b);
    const scoreRank = Object.fromEntries(uniqueScores.map((s, i) => [s, i + 1]));
    const map = {};
    scores.forEach(s => { map[s.id] = s.score === 0 ? '' : scoreRank[s.score]; });
    return map;
  };

  const originalPlaces = getPlaces(rowsBase, 'originalTotal');
  const adjustedPlaces = getPlaces(rowsBase, 'adjustedTotal');

  const rows = rowsBase.map(r => ({
    ...r,
    categoryPlace: originalPlaces[r.id] || '',
    categoryPlaceAssociated: adjustedPlaces[r.id] || ''
  }));

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
        <Box sx={{ flex: 0.75 }}>
          {/* Category summary card styled like EventSummaryCard */}
          <CategorySummaryCard
            categoryUid={selectedCategory}
            categoriesMap={categoriesMap}
            divisionsMap={divisionsMap}
            adjustedTeamsMap={adjustedTeamsMap}
            eventsScores={eventsScores}
            affiliatedMode={affiliatedMode}
          />
        </Box>
      </Box>

      <DataGrid
        rows={rows}
        columns={columns}
        slots={{ toolbar: GridToolbar }}
        pageSizeOptions={[25, 50, 100]}
        initialState={{ pagination: { paginationModel: { pageSize: 25 } }, sorting: { sortModel: [{ field: (eventHasAdjustments ? 'categoryPlaceAssociated' : 'categoryPlace'), sort: 'asc' }] } }}
      />
    </Box>
  );
}
